import { useMemo } from 'react';

import SubActivityPrint from '../../sub-activity/sub-activity-print';
import {
  CATEGORISE_DROP_ITEM_SIZE,
  RESPONSE_FREE,
  ViewProps,
} from './interactive-response-categorise.constants';
import {
  getAnswers,
  initialise,
} from './play/interactive-response-categorise-play.utils';
import {
  InteractiveResponseAnswerIcons,
  InteractiveResponseTitle,
  InteractiveResponseViewScaler,
} from '../shared/interactive-response-shared';
import { InteractiveResponseContainer } from '../interactive-response.style';
import InteractiveDragDisplay from '../../shared/interactive-drag-drop/interactive-drag-display';
import { ComponentAspectRatio } from '../../shared/component-height/component-dimensions';
import { ComponentAbsoluteWrapper } from '../../shared/component-height/component-dimensions.style';
import { useContainerDimensions } from '../../shared/component-height/component-dimensions.hooks';
import { useActivityAnswersPrintContext } from 'approot/activity-builder/print/answers/activity-answers-print.context';
import { defaultDimensions } from '../interactive-response-shared.constants';
import { InteractiveDropItemTarget } from 'approot/activity-builder/components/interactive-response/categorise/play/interactive-response-categorise-drop.style';
import { InteractiveDropItem } from 'approot/activity-builder/components/interactive-response/categorise/play/interactive-response-categorise-drop.style';
import { InteractiveResponsePrintCategory } from './interactive-response-categorise-print.style';
import type { InteractiveResponseAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import { InteractiveDropDisplay } from './play/interactive-response-categorise-drop-display';
import * as Styled from './interactive-response-categorise-print.style';
import { Box } from 'approot/shared/design-system/box/box.style';

const InteractiveResponseCategorisePrintAnswers = ({
  id,
  properties,
  subActivityProperties,
  studentId,
}: ViewProps & {
  studentId?: number;
}) => {
  const interactiveResponse = properties.interactiveResponseCategorise;
  const isFreeResponse = interactiveResponse.answerType === RESPONSE_FREE;

  const { answers, showAnswer } = useActivityAnswersPrintContext(id);

  const answer = useMemo(
    () =>
      studentId && answers
        ? (answers?.find(a => a.studentId === studentId)
            ?.answer as InteractiveResponseAnswerFragment)
        : (answers?.[0]?.answer as InteractiveResponseAnswerFragment),
    [answers, studentId]
  );

  const parsedAnswers = useMemo(() => {
    if (!answer) {
      return null;
    }

    return getAnswers(
      answer,
      interactiveResponse.items,
      interactiveResponse.categories,
      showAnswer
    );
  }, [
    interactiveResponse.items,
    interactiveResponse.categories,
    answer,
    showAnswer,
  ]);

  const { dragItems, dropItems } = useMemo(() => {
    return initialise({
      items: interactiveResponse.items,
      categories: interactiveResponse.categories,
      showAnswer: showAnswer,
      isShowNumberOfItemsHint: !!interactiveResponse.showNumberOfItemsHint,
      shouldShuffle: true,
      variant: interactiveResponse.variant,
    });
  }, [
    showAnswer,
    interactiveResponse.items,
    interactiveResponse.categories,
    interactiveResponse.showNumberOfItemsHint,
    interactiveResponse.variant,
  ]);

  const { setContainerElement } = useContainerDimensions({
    aspectRatio: 16 / 9,
  });

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <Box $justifyContent="center">
        <InteractiveResponseContainer>
          <ComponentAspectRatio
            dimensions={properties.categoriseDimensionData || defaultDimensions}
          >
            <ComponentAbsoluteWrapper ref={setContainerElement}>
              <InteractiveResponseViewScaler>
                <InteractiveResponseTitle
                  title={interactiveResponse.title}
                  subTitle={interactiveResponse.subTitle}
                />

                {parsedAnswers ? (
                  <Styled.InteractiveResponsePrintCategories>
                    {parsedAnswers.map(answer => {
                      return (
                        <InteractiveResponsePrintCategory key={answer.id}>
                          <Styled.InteractiveResponsePrintCategoryLabel>
                            {answer.label}
                          </Styled.InteractiveResponsePrintCategoryLabel>
                          <Styled.InteractiveResponsePrintCategoriseDropTarget>
                            <Styled.InteractiveResponsePrintDragZone $gap={8}>
                              {answer.zones.map(zone => {
                                const droppedDragItem =
                                  interactiveResponse.items.find(
                                    item => item.itemId === zone.dragItemId
                                  );
                                return (
                                  <InteractiveDropItem key={zone.dragItemId}>
                                    <InteractiveDropItemTarget
                                      $fullHeight
                                      $fullWidth
                                    >
                                      {!isFreeResponse && showAnswer && (
                                        <InteractiveResponseAnswerIcons
                                          isCorrect={zone.isCorrect}
                                          animate={false}
                                        />
                                      )}
                                      <InteractiveDropDisplay
                                        isContinuousMarking={false}
                                        isCorrect={
                                          isFreeResponse || !showAnswer
                                            ? undefined
                                            : zone.isCorrect
                                        }
                                        hasDropped
                                      >
                                        <InteractiveDragDisplay
                                          scaleTo={CATEGORISE_DROP_ITEM_SIZE}
                                          imageUrl={zone.previewItemId}
                                          isFit={
                                            droppedDragItem?.imageMode === 'fit'
                                          }
                                          description={
                                            droppedDragItem?.image
                                              ?.description || ''
                                          }
                                          baseColour={
                                            droppedDragItem?.baseColour || null
                                          }
                                          variant={
                                            droppedDragItem?.variant ||
                                            interactiveResponse.variant
                                          }
                                          text={droppedDragItem?.text || ''}
                                          richtext={
                                            droppedDragItem?.richtext || null
                                          }
                                        />
                                      </InteractiveDropDisplay>
                                    </InteractiveDropItemTarget>
                                  </InteractiveDropItem>
                                );
                              })}
                            </Styled.InteractiveResponsePrintDragZone>
                          </Styled.InteractiveResponsePrintCategoriseDropTarget>
                        </InteractiveResponsePrintCategory>
                      );
                    })}
                  </Styled.InteractiveResponsePrintCategories>
                ) : (
                  <>
                    <Styled.InteractiveResponsePrintDragZone>
                      {dragItems.map((dragItem, index) => (
                        <Styled.InteractiveResponsePrintDragZoneItem
                          key={`${dragItem.id}-${index}`}
                        >
                          <InteractiveDragDisplay
                            imageUrl={dragItem.question.image?.small}
                            isFit={dragItem.question.image?.isFit}
                            description={
                              dragItem.question.image?.description || ''
                            }
                            baseColour={dragItem.question.baseColour}
                            variant={dragItem.question.variant}
                            text={dragItem.question.text}
                            richtext={dragItem.question.richtext}
                          />
                        </Styled.InteractiveResponsePrintDragZoneItem>
                      ))}
                    </Styled.InteractiveResponsePrintDragZone>
                    <Styled.InteractiveResponsePrintCategories>
                      {dropItems.map(dropItem => {
                        return (
                          <Styled.InteractiveResponsePrintCategory
                            key={dropItem.id}
                          >
                            <Styled.InteractiveResponsePrintCategoryLabel>
                              {dropItem.label}
                            </Styled.InteractiveResponsePrintCategoryLabel>
                            <Styled.InteractiveResponsePrintCategoriseDropTarget></Styled.InteractiveResponsePrintCategoriseDropTarget>
                          </Styled.InteractiveResponsePrintCategory>
                        );
                      })}
                    </Styled.InteractiveResponsePrintCategories>
                  </>
                )}
              </InteractiveResponseViewScaler>
            </ComponentAbsoluteWrapper>
          </ComponentAspectRatio>
        </InteractiveResponseContainer>
      </Box>
    </SubActivityPrint>
  );
};

export default InteractiveResponseCategorisePrintAnswers;
